"use client";

import { useState } from "react";
import { MapPin, Store, Loader2, Sparkles } from "lucide-react";

export interface Retailer {
  id: string;
  businessName: string;
  distance: number;
  logoUrl?: string;
  brandingConfig?: any;
  address?: string;
  city?: string;
  state?: string;
}

interface RetailerSelectorProps {
  retailers: Retailer[];
  onSelectRetailer: (retailerId: string) => Promise<void>;
  onSelectGeneral: () => Promise<void>;
  loading?: boolean;
  primaryColor?: string;
  zipcode?: string;
}

export function RetailerSelector({
  retailers,
  onSelectRetailer,
  onSelectGeneral,
  loading = false,
  primaryColor = "#10b981", // emerald-500 default
  zipcode,
}: RetailerSelectorProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelectRetailer = async (retailerId: string) => {
    setSelectedId(retailerId);
    setIsSubmitting(true);

    try {
      await onSelectRetailer(retailerId);
    } catch (error) {
      console.error("Failed to select retailer:", error);
      setSelectedId(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectGeneral = async () => {
    setSelectedId("general");
    setIsSubmitting(true);

    try {
      await onSelectGeneral();
    } catch (error) {
      console.error("Failed to select general:", error);
      setSelectedId(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="w-full max-w-3xl mx-auto space-y-6 px-4 md:px-0">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-plantista-100 dark:bg-plantista-800 mb-2 md:mb-4">
            <Loader2 className="w-6 h-6 md:w-8 md:h-8 text-plantista-600 dark:text-plantista-400 animate-spin" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-plantista-950 dark:text-white">
            Finding nearby retailers...
          </h2>
          <p className="text-sm md:text-base text-plantista-600 dark:text-plantista-300">
            Searching for garden centers in your area
          </p>
        </div>

        <div className="bg-white dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-xl shadow-lg p-4 md:p-6 space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse flex items-center space-x-3 md:space-x-4 p-3 md:p-4 border-2 border-gray-200 dark:border-plantista-800 rounded-lg"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-200 dark:bg-plantista-800 rounded-lg flex-shrink-0"></div>
              <div className="flex-1 space-y-2 min-w-0">
                <div className="h-4 md:h-5 bg-gray-200 dark:bg-plantista-800 rounded w-3/4"></div>
                <div className="h-3 md:h-4 bg-gray-200 dark:bg-plantista-800 rounded w-1/2"></div>
              </div>
              <div className="hidden md:block w-24 h-10 bg-gray-200 dark:bg-plantista-800 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (retailers.length === 0) {
    return (
      <div className="w-full max-w-3xl mx-auto px-4 md:px-0">
        <div className="bg-white dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-xl shadow-lg p-6 md:p-8 space-y-6">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-plantista-100 dark:bg-plantista-800 mb-2 md:mb-4">
              <Store className="w-6 h-6 md:w-8 md:h-8 text-plantista-600 dark:text-plantista-400" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-plantista-950 dark:text-white">
              No retailers found nearby
            </h2>
            <p className="text-sm md:text-base text-plantista-600 dark:text-plantista-300">
              We couldn't find any garden centers in your area
              {zipcode ? ` (${zipcode})` : ""}.
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-plantista-950 border border-gray-200 dark:border-plantista-800 rounded-lg p-4">
            <p className="text-sm text-plantista-700 dark:text-plantista-300 text-center">
              Don't worry! You can still use the general Plantista Designer with
              our full catalog of plants.
            </p>
          </div>

          <button
            onClick={handleSelectGeneral}
            disabled={isSubmitting}
            className={`
              w-full px-6 py-4 rounded-lg font-semibold text-white dark:text-plantista-950
              transition-all duration-200
              focus:outline-none focus:ring-4 focus:ring-plantista-500/50 dark:focus:ring-plantista-900
              ${
                isSubmitting
                  ? "bg-gray-300 dark:bg-plantista-800 text-gray-500 dark:text-plantista-600 cursor-not-allowed"
                  : "bg-plantista-500 dark:bg-plantista-400 hover:bg-plantista-600 dark:hover:bg-plantista-300 active:scale-95"
              }
            `}
            style={{
              backgroundColor: isSubmitting ? undefined : primaryColor,
            }}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center space-x-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading...</span>
              </span>
            ) : (
              <span className="flex items-center justify-center space-x-2">
                <Sparkles className="w-5 h-5" />
                <span>Use General Plantista Designer</span>
              </span>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Retailer list
  return (
    <div className="w-full max-w-3xl mx-auto space-y-6 px-4 md:px-0">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-full bg-plantista-100 dark:bg-plantista-800 mb-2 md:mb-4">
          <Store className="w-6 h-6 md:w-8 md:h-8 text-plantista-600 dark:text-plantista-400" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-plantista-950 dark:text-white">
          Select your garden center
        </h2>
        <p className="text-sm md:text-base text-plantista-600 dark:text-plantista-300">
          Found {retailers.length} retailer{retailers.length !== 1 ? "s" : ""}{" "}
          near {zipcode || "you"}
        </p>
      </div>

      {/* Retailer Cards */}
      <div className="bg-white dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-xl shadow-lg p-4 md:p-6 space-y-4">
        {retailers.map((retailer, index) => (
          <div key={retailer.id}>
            <button
              onClick={() => handleSelectRetailer(retailer.id)}
              disabled={isSubmitting}
              className={`
                w-full flex items-center space-x-3 md:space-x-4 p-3 md:p-4
                border-2 rounded-lg transition-all
                focus:outline-none focus:ring-4 focus:ring-plantista-500/50 dark:focus:ring-plantista-900
                ${
                  isSubmitting
                    ? "cursor-not-allowed opacity-60"
                    : "hover:border-plantista-500 dark:hover:border-plantista-400 hover:shadow-md active:scale-[0.99]"
                }
                ${
                  selectedId === retailer.id
                    ? "border-plantista-500 dark:border-plantista-400 bg-plantista-50 dark:bg-plantista-800"
                    : "border-gray-200 dark:border-plantista-800 bg-white dark:bg-plantista-950 hover:bg-gray-50 dark:hover:bg-plantista-900"
                }
              `}
            >
              {/* Logo */}
              <div className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0 rounded-full bg-white border-2 border-gray-200 dark:border-plantista-800 flex items-center justify-center p-1 md:p-2">
                {retailer.logoUrl ? (
                  <img
                    src={retailer.logoUrl}
                    alt={`${retailer.businessName} logo`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      const parent = target.parentElement;
                      if (parent) {
                        const fallback = document.createElement("div");
                        fallback.className =
                          "w-6 h-6 md:w-8 md:h-8 text-gray-400";
                        fallback.innerHTML =
                          '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>';
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                ) : (
                  <Store className="w-6 h-6 md:w-8 md:h-8 text-gray-400" />
                )}
              </div>

              {/* Retailer Info */}
              <div className="flex-1 text-left min-w-0">
                <h3 className="font-semibold text-plantista-950 dark:text-white text-base md:text-lg truncate">
                  {retailer.businessName}
                </h3>
                <div className="flex items-center space-x-2 mt-0.5">
                  <MapPin className="w-3 h-3 md:w-4 md:h-4 text-plantista-500 dark:text-plantista-400" />
                  <span className="text-xs md:text-sm text-plantista-600 dark:text-plantista-300">
                    {retailer.distance.toFixed(1)} miles away
                  </span>
                </div>
              </div>

              {/* Select Button - Hidden on mobile, shown on md+ */}
              <div className="flex-shrink-0 hidden md:block">
                {selectedId === retailer.id && isSubmitting ? (
                  <div className="w-24 h-10 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-plantista-500 dark:text-plantista-400" />
                  </div>
                ) : (
                  <div
                    className="px-6 py-2 rounded-lg font-medium text-white dark:text-plantista-950 bg-plantista-500 dark:bg-plantista-400 hover:bg-plantista-600 dark:hover:bg-plantista-300 transition"
                    style={{
                      backgroundColor: primaryColor,
                    }}
                  >
                    Select
                  </div>
                )}
              </div>
              {/* Mobile Arrow */}
              <div className="md:hidden flex-shrink-0">
                <svg
                  className="w-5 h-5 text-gray-400 dark:text-plantista-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </button>

            {/* Divider between retailers */}
            {index < retailers.length - 1 && (
              <div className="h-px bg-gray-200 dark:bg-plantista-800 my-4 mx-4"></div>
            )}
          </div>
        ))}

        {/* Divider with OR */}
        <div className="relative py-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200 dark:border-plantista-800"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white dark:bg-plantista-900 text-gray-500 dark:text-plantista-400 font-medium">
              OR
            </span>
          </div>
        </div>

        {/* General Designer Option */}
        <button
          onClick={handleSelectGeneral}
          disabled={isSubmitting}
          className={`
            w-full px-6 py-4 rounded-lg font-medium
            border-2 transition-all
            focus:outline-none focus:ring-4 focus:ring-plantista-500/50 dark:focus:ring-plantista-900
            ${
              isSubmitting
                ? "border-gray-200 dark:border-plantista-800 bg-gray-100 dark:bg-plantista-900 text-gray-400 dark:text-plantista-600 cursor-not-allowed"
                : "border-plantista-200 dark:border-plantista-700 bg-white dark:bg-plantista-950 text-plantista-700 dark:text-plantista-300 hover:border-plantista-500 dark:hover:border-plantista-500 hover:bg-plantista-50 dark:hover:bg-plantista-900 hover:text-plantista-900 dark:hover:text-white active:scale-[0.99]"
            }
            ${selectedId === "general" ? "border-plantista-500 dark:border-plantista-400 bg-plantista-50 dark:bg-plantista-800" : ""}
          `}
        >
          {selectedId === "general" && isSubmitting ? (
            <span className="flex items-center justify-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading...</span>
            </span>
          ) : (
            <span className="flex items-center justify-center space-x-2">
              <Sparkles className="w-5 h-5" />
              <span>Use General Plantista Designer</span>
            </span>
          )}
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-plantista-50/50 dark:bg-plantista-900/50 border border-plantista-200 dark:border-plantista-800 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <svg
            className="w-5 h-5 text-plantista-500 dark:text-plantista-400 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm text-plantista-700 dark:text-plantista-300">
            <p className="font-medium mb-1">Why choose a garden center?</p>
            <p className="text-plantista-600 dark:text-plantista-400">
              Garden centers provide curated plant selections optimized for your
              local climate and can help you source the plants from your design.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
